import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import "../styles/Services.css";

export default function Services() {
  const navigate = useNavigate();

  const handleBook = () => {
    navigate("/book");
  };

  /* =========================================================
     Structured Data
  ========================================================= */

  const ldLocalBusiness = {
    "@context": "https://schema.org",
    "@type": "BeautySalon",
    name: "Lash With Brittany",
    url: "https://lashwithbrittany.com/services",
    priceRange: "$$",
    areaServed: {
      "@type": "City",
      name: "Rochester",
      address: {
        "@type": "PostalAddress",
        addressRegion: "NY",
        addressCountry: "US",
      },
    },
  };

  const ldBreadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://lashwithbrittany.com/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://lashwithbrittany.com/services" },
    ],
  };

  const ldFAQ = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How often do I need a fill",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Most clients return every two to three weeks. Timing depends on your natural lash cycle, aftercare routine, and lifestyle."
        }
      },
      {
        "@type": "Question",
        name: "What is a lash lift",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "A lash lift curls your natural lashes upward, giving a brighter and more open look. Results typically last several weeks with proper care."
        }
      },
      {
        "@type": "Question",
        name: "Can I come in with extensions from another salon",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Yes. For a new client fill, at least forty percent of extensions should remain. Depending on lash health, a removal and fresh set may be recommended."
        }
      },
      {
        "@type": "Question",
        name: "How should I prepare for my appointment",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Arrive with clean lashes free of mascara, oils, or makeup. Avoid caffeine before your appointment so you can relax comfortably."
        }
      },
      {
        "@type": "Question",
        name: "Do you take new clients",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Yes. New clients are always welcome and can book online at any time."
        }
      }
    ]
  };

  return (
    <main className="services-container">
      <Helmet>
        <title>Lash Extensions and Beauty Services in Rochester NY | Lash With Brittany</title>
        <meta
          name="description"
          content="Book lash extensions, lash lifts, waxing, brows, and permanent jewelry in Rochester NY. Trusted lash artist Brittany delivers clean, long lasting results."
        />
        <link rel="canonical" href="https://lashwithbrittany.com/services" />
        <script type="application/ld+json">{JSON.stringify(ldLocalBusiness)}</script>
        <script type="application/ld+json">{JSON.stringify(ldBreadcrumbs)}</script>
        <script type="application/ld+json">{JSON.stringify(ldFAQ)}</script>
      </Helmet>

      {/* ========================= Header ========================= */}
      <div className="page-header-border-wrapper">
        <header className="page-header-card">
          <p className="hero-tagline">Transform Your Look. Confidence Starts Here.</p>

          <h1 className="seo-title">
            Rochester NY Lash Extensions, Brows, Waxing & Permanent Jewelry
          </h1>

          <p className="page-sub">
            Beautiful lashes, brows, waxing, and permanent jewelry in a comfort first Rochester studio.
          </p>

          <div className="action-row">
            <button className="primary-cta" onClick={handleBook} aria-label="Book now - Services">Book Now</button>
            <a href="#faq" className="secondary-cta" aria-label="Read frequently asked questions">Read FAQs</a>
          </div>
        </header>
      </div>

      {/* ========================= Jump Links ========================= */}
      <nav className="jump-links" aria-label="Jump to category">
        <a href="#full-sets">Full Sets</a>
        <a href="#fills">Fills</a>
        <a href="#other-lash">Other Lash Services</a>
        <a href="#permanent-jewelry">Permanent Jewelry</a>
        <a href="#other-facial-services">Other Facial Services</a>
        <a href="#facial-waxing">Facial Waxing</a>
        <a href="#body-waxing">Body Waxing</a>
      </nav>

      {/* ========================= Services ========================= */}
      <section className="services-list">

        {/* Full Sets */}
        <h2 id="full-sets" className="category-header">Full Sets</h2>

        <Service
          title="Classic Full Set Lash Extensions"
          description="Individual extensions are applied to each natural lash for a soft, clean, and natural looking enhancement that works beautifully for everyday wear."
          price="$175.00"
          onBook={handleBook}
        />

        <Service
          title="Volume Hybrid Full Set Lash Extensions"
          description="A blend of classic lashes and lightweight volume fans for fuller definition while maintaining balance and softness."
          price="$200.00"
          onBook={handleBook}
        />

        <Service
          title="Mega Volume Full Set Lash Extensions"
          description="Ultra lightweight fans are used to create bold, dramatic volume with dense coverage. Ideal for clients who love a statement look."
          price="$250.00"
          onBook={handleBook}
        />

        {/* Fills */}
        <h2 id="fills" className="category-header">Fills</h2>

        <ServiceList
          title="Fill 1 Week"
          description="Ideal when minimal shedding has occurred and only light maintenance is needed."
          items={[
            ["Classic", "$45.00"],
            ["Volume Hybrid", "$50.00"],
            ["Mega Volume", "$55.00"],
          ]}
          onBook={handleBook}
        />

        <ServiceList
          title="Fill 2 Weeks"
          description="Refresh fullness after normal lash shedding to maintain shape and density."
          items={[
            ["Classic", "$60.00"],
            ["Volume Hybrid", "$65.00"],
            ["Mega Volume", "$70.00"],
          ]}
          onBook={handleBook}
        />

        <ServiceList
          title="Fill 3 Weeks"
          description="Maintain your lash set as natural lashes continue their growth cycle."
          items={[
            ["Classic", "$70.00"],
            ["Volume Hybrid", "$75.00"],
            ["Mega Volume", "$85.00"],
          ]}
          onBook={handleBook}
        />

        <ServiceList
          title="Fill 4 Weeks"
          description="Extended fill option for clients with fewer remaining extensions."
          items={[
            ["Classic", "$85.00"],
            ["Volume Hybrid", "$95.00"],
            ["Mega Volume", "$105.00"],
          ]}
          onBook={handleBook}
        />

        <Service
          title="New Client Fill"
          description="For clients with extensions from another salon. At least forty percent of extensions should remain to qualify."
          price="$85.00"
          onBook={handleBook}
        />

        {/* Other Lash Services */}
        <h2 id="other-lash" className="category-header">Other Lash Services</h2>

        <Service
          title="Lash Lift"
          description="A gentle curling treatment that enhances your natural lashes for a lifted and eye opening effect."
          price="$75.00"
          onBook={handleBook}
        />

        <Service
          title="Lash Bath"
          description="A deep cleanse that removes oils and buildup to help extensions last longer."
          price="$15.00"
          onBook={handleBook}
        />

        <Service
          title="Lash Extension Removal"
          description="Safe and careful removal of extensions while protecting natural lash health."
          price="$35.00"
          onBook={handleBook}
        />

        {/* Permanent Jewelry */}
        <h2 id="permanent-jewelry" className="category-header">Permanent Jewelry</h2>

        <Service
          title="Permanent Jewelry Wrist"
          description="Custom fit bracelet welded for a seamless, clasp free finish."
          price="$60.00"
          onBook={handleBook}
        />

        <Service
          title="Permanent Jewelry Ankle"
          description="An elegant anklet welded for a secure and timeless look."
          price="$70.00"
          onBook={handleBook}
        />

        <Service
          title="Permanent Jewelry Necklace"
          description="Custom measured necklace welded for everyday wear."
          price="$90.00"
          onBook={handleBook}
        />

        <Service
          title="Permanent Jewelry Hand Chain"
          description="A unique hand chain designed and fitted specifically for you."
          price="$75.00"
          onBook={handleBook}
        />

        {/* Other Facial Services */}
        <h2 id="other-facial-services" className="category-header">Other Facial Services</h2>

        <Service
          title="Brow Sculpt with Tint"
          description="Brows are shaped and tinted to create fuller, more defined arches."
          price="$35.00"
          onBook={handleBook}
        />

        <Service
          title="Brow Lamination"
          description="A smoothing treatment that sets brow hairs in place for a lifted and uniform appearance."
          price="$70.00"
          onBook={handleBook}
        />

        {/* Facial Waxing */}
        <h2 id="facial-waxing" className="category-header">Facial Waxing</h2>

        <Service
          title="Eyebrow Waxing"
          description="Precise shaping to enhance your natural brow line."
          price="$18.00"
          onBook={handleBook}
        />

        <Service
          title="Lip Waxing"
          description="Removes unwanted hair on the upper lip for a smooth finish."
          price="$14.00"
          onBook={handleBook}
        />

        <Service
          title="Chin Waxing"
          description="Quick cleanup for a polished and even look."
          price="$14.00"
          onBook={handleBook}
        />

        <Service
          title="Side of Face Waxing"
          description="Smooths the sides of the face for a soft and refined appearance."
          price="$16.00"
          onBook={handleBook}
        />

        <Service
          title="Cheeks Waxing"
          description="Removes hair on the cheeks for a clean, even glow."
          price="$15.00"
          onBook={handleBook}
        />

        <Service
          title="Full Face Waxing"
          description="A complete facial waxing service for a refreshed look."
          price="$45.00"
          onBook={handleBook}
        />

        <Service
          title="Nose Waxing"
          description="Removes unwanted hair from the nose area safely and quickly."
          price="$15.00"
          onBook={handleBook}
        />

        {/* Body Waxing */}
        <h2 id="body-waxing" className="category-header">Body Waxing</h2>

        <Service
          title="Underarms Waxing"
          description="Fast and effective hair removal for smooth underarms."
          price="$20.00"
          onBook={handleBook}
        />

        <Service
          title="Half Leg Waxing"
          description="Removes hair from the lower legs for a sleek finish."
          price="$35.00"
          onBook={handleBook}
        />

        <Service
          title="Full Leg Waxing"
          description="Complete leg waxing for smooth results from top to bottom."
          price="$70.00"
          onBook={handleBook}
        />

      </section>

      {/* ========================= FAQ ========================= */}
      <section id="faq" className="faq">
        <h2>Frequently Asked Questions</h2>

        <details>
          <summary>How often do I need a fill</summary>
          <p>Most clients return every two to three weeks depending on natural lash growth.</p>
        </details>

        <details>
          <summary>What is a lash lift</summary>
          <p>A lash lift curls your natural lashes for a brighter, more open look.</p>
        </details>

        <details>
          <summary>Can I come in with extensions from another salon</summary>
          <p>Yes, as long as enough extensions remain. A fresh set may be recommended based on lash health.</p>
        </details>

        <details>
          <summary>How should I prepare for my appointment</summary>
          <p>Arrive with clean lashes free of makeup or oils.</p>
        </details>

        <details>
          <summary>Do you take new clients</summary>
          <p>Yes. New clients are always welcome.</p>
        </details>
      </section>
    </main>
  );
}

/* =========================================================
   Helpers
========================================================= */

function Service({ title, description, price, onBook }) {
  return (
    <div className="service-item" role="button" tabIndex={0} onClick={onBook}>
      <h3>{title}</h3>
      <p>{description}</p>
      <span className="price">{price}</span>
      <p className="cta-text">Book now</p>
    </div>
  );
}

function ServiceList({ title, description, items, onBook }) {
  return (
    <div className="service-item" role="button" tabIndex={0} onClick={onBook}>
      <h3>{title}</h3>
      <p>{description}</p>
      <ul>
        {items.map(([label, price]) => (
          <li key={label}>
            <span>{label}</span>
            <span>{price}</span>
          </li>
        ))}
      </ul>
      <p className="cta-text">Book now</p>
    </div>
  );
}
